import React, {useEffect, useRef, useState} from "react";
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Container,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";

export default function ClientForm(props) {
    const [loaded, setLoaded] = useState(false);
    const editionMode = props.editionMode || false;
    const [clientType, setClientType] = useState("particulier");
    const [formData, setFormData] = useState({
        companyName: "",
        siret: "",
        tva: "",
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        address: "",
        postalCode: "",
        city: "",
        notes: "",
    });
    const [loading, setLoading] = useState(false);

    // Autocomplete states
    const [suggestions, setSuggestions] = useState([]);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const abortRef = useRef(null);
    const debounceRef = useRef(null);

    const handleTypeChange = (e) => {
        const type = e.target.value;
        setClientType(type);
    };

    const handleChange = (e) => {
        const {name, value} = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleCompanyInputChange = (event, newInputValue) => {
        // Met à jour le champ companyName dans formData pour déclencher le fetch via useEffect
        setFormData((prev) => ({
            ...prev,
            companyName: newInputValue,
        }));
    };

    const generateTvaNumber = siren => {
        const cleanSiren = String(siren).replace(/\s+/g, '');

        if (cleanSiren.length !== 9 || isNaN(cleanSiren)) {
            return null; // SIREN invalide
        }

        // Calcul de la clé de contrôle (tvaKey)
        // Note : On utilise le modulo (%)
        const tvaKey = (12 + 3 * (cleanSiren % 97)) % 97;

        // 3. Formatage : La clé doit avoir 2 chiffres (ex: 5 devient "05")
        const tvaKeyFormatted = String(tvaKey).padStart(2, '0');

        // 4. Concaténation finale
        return `FR${tvaKeyFormatted}${cleanSiren}`;
    }

    const handleSelectSuggestion = (event, value) => {
        if (!value) return;
        const data = value.raw || {};
        // Récupérations robustes des champs possibles
        const siret = data.siren || "";

        let address = data.siege.adresse;
        const postalCode = data.siege.code_postal;
        const city = data.siege.libelle_commune;
        const tva = generateTvaNumber(siret);

        // On récupère l'adresse sans le code postal et la ville
        address = address
            .replace(postalCode, "")
            .replace(city, "")
            .trim()

        setFormData((prev) => ({
            ...prev,
            companyName: value.label,
            siret: siret,
            tva: tva,
            address: address,
            postalCode: postalCode,
            city: city,
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (loading) return; // Empêche les soumissions multiples
        setLoading(true);

        // Contrôle des champs requis
        if (!e.currentTarget.checkValidity()) {
            e.currentTarget.reportValidity();
            setLoading(false);
            return;
        }

        props.onSubmit(formData).finally(() => {
            setLoading(false);
        });
    };

    useEffect(() => {
        if (editionMode) {
            if (!props.data) return;
            setFormData(props.data);
            setClientType(props.data.companyName ? "professionnel" : "particulier");
        }

        setLoaded(true);
    }, [props.data, editionMode]);

    // Fetch suggestions quand on tape le nom de l'entreprise (debounced)
    useEffect(() => {
        const query = formData.companyName?.trim();

        // On ne fait la recherche que pour les professionnels et si la query est assez longue
        if (clientType !== "professionnel" || !query || query.length < 3) {
            setSuggestions([]);
            setLoadingSuggestions(false);
            return;
        }

        // Debounce
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            // Abort previous request
            if (abortRef.current) {
                abortRef.current.abort();
            }
            const controller = new AbortController();
            abortRef.current = controller;

            setLoadingSuggestions(true);

            const url = `https://recherche-entreprises.api.gouv.fr/search?q=${encodeURIComponent(query)}&page=1&per_page=10&include=siege&minimal=true`;

            fetch(url, {signal: controller.signal})
                .then((res) => res.json())
                .then((data) => {
                    // Le format peut varier; chercher les tableaux communs
                    const hits = data.etablissements || data.results || data.items || data.hits || data.records || [];
                    const mapped = (Array.isArray(hits) ? hits : []).map((item) => {
                        // Certains formats imbriquent la donnée dans 'etablissement' ou 'fields'
                        const raw = item || {};
                        // Normaliser label
                        const label =
                            raw.denomination || raw.nom_raison_sociale || raw.nom || raw.nom_entreprise || raw.name ||
                            (raw.siret ? `${raw.siret} - ${raw.nom}` : raw.libelle) || (raw.fields && (raw.fields.denomination || raw.fields.name)) ||
                            query;
                        return {label, raw};
                    });
                    setSuggestions(mapped);
                })
                .catch((err) => {
                    if (err.name === "AbortError") return;
                    console.error("Erreur recherche entreprises:", err);
                    setSuggestions([]);
                })
                .finally(() => {
                    setLoadingSuggestions(false);
                });
        }, 350);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            // abort handled at next call
        };
    }, [formData.companyName, clientType]);

    return (
        loaded ? (
                <form component="form" onSubmit={e => handleSubmit(e)} noValidate>
                    <Stack spacing={3}>
                        {/* Type de client */}
                        <FormControl fullWidth>
                            <InputLabel id="client-type-label">Type de client</InputLabel>
                            <Select
                                labelId="client-type-label"
                                value={clientType}
                                onChange={handleTypeChange}
                                label="Type de client"
                                disabled={editionMode}
                            >
                                <MenuItem value="particulier">Particulier</MenuItem>
                                <MenuItem value="professionnel">Professionnel</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Infos client */}
                        {clientType === "professionnel" ? (
                            <>
                                <Autocomplete
                                    filterOptions={e => e}
                                    freeSolo
                                    disabled={editionMode}
                                    options={suggestions}
                                    loading={loadingSuggestions}
                                    onInputChange={handleCompanyInputChange}
                                    onChange={handleSelectSuggestion}
                                    renderOption={(props, option) => (
                                        <li
                                            {...props}
                                            key={option.raw?.siren}
                                        >
                                            {option.label}, {option.raw?.siege?.geo_adresse || option.raw?.siege?.adresse || ""}
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            name="companyName"
                                            label="Nom de l'entreprise"
                                            value={formData.companyName}
                                            onChange={() => {
                                            }}
                                            fullWidth
                                            required
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {loadingSuggestions ?
                                                            <CircularProgress color="inherit" size={20}/> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />

                                <Stack direction={{xs: "column", sm: "row"}} spacing={2}>
                                    <TextField
                                        name="siret"
                                        label="Numéro SIRET"
                                        value={formData.siret}
                                        onChange={handleChange}
                                        fullWidth
                                        sx={{flexGrow: 1}}
                                    />
                                    <TextField
                                        name="tva"
                                        label="N° TVA intracommunautaire"
                                        value={formData.tva}
                                        onChange={handleChange}
                                        fullWidth
                                        sx={{flexGrow: 1}}
                                    />
                                </Stack>
                            </>
                        ) : (
                            <Stack direction={{xs: "column", sm: "row"}} spacing={2}>
                                <TextField
                                    name="firstName"
                                    label="Prénom"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    fullWidth
                                    sx={{flexGrow: 1}}
                                />
                                <TextField
                                    name="lastName"
                                    label="Nom"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    fullWidth
                                    sx={{flexGrow: 1}}
                                    required
                                />
                            </Stack>
                        )}

                        {/* Coordonnées */}
                        <Stack direction={{xs: "column", sm: "row"}} spacing={2}>
                            <TextField
                                name="email"
                                label="Adresse email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                                fullWidth
                                sx={{flexGrow: 1}}
                                required
                            />
                            <TextField
                                name="phone"
                                label="Téléphone"
                                value={formData.phone}
                                onChange={handleChange}
                                fullWidth
                                sx={{flexGrow: 1}}
                            />
                        </Stack>

                        {/* Adresse */}
                        <Typography variant="h6" sx={{mt: 2}}>
                            Adresse
                        </Typography>
                        <Stack direction={{xs: "column", sm: "row"}} spacing={2}>
                            <TextField
                                name="address"
                                label="Adresse (n° et rue)"
                                value={formData.address}
                                onChange={handleChange}
                                fullWidth
                                sx={{flexGrow: 2}}
                            />
                            <TextField
                                name="postalCode"
                                label="Code postal"
                                value={formData.postalCode}
                                onChange={handleChange}
                                fullWidth
                                sx={{flexGrow: 1}}
                                required
                            />
                            <TextField
                                name="city"
                                label="Ville"
                                value={formData.city}
                                onChange={handleChange}
                                fullWidth
                                sx={{flexGrow: 2}}
                            />
                        </Stack>

                        {/* Notes internes */}
                        <TextField
                            name="notes"
                            label="Notes internes"
                            value={formData.notes}
                            onChange={handleChange}
                            multiline
                            rows={3}
                            fullWidth
                        />

                        <Button type="submit" variant="contained" size="large" disabled={loading}>
                            {loading ? "Enregistrement..." : "Enregistrer le client"}
                        </Button>
                    </Stack>
                </form>
            )
            : (
                <Container maxWidth="lg"
                           sx={{mt: 4, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh"}}>
                    <Stack spacing={2} alignItems="center">
                        <Box>
                            <Typography variant="h6" sx={{mb: 2}}>Chargement des détails...</Typography>
                            <Box sx={{display: "flex", justifyContent: "center"}}>
                                <CircularProgress color="primary"/>
                            </Box>
                        </Box>
                    </Stack>
                </Container>
            )
    )
}
